import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatAgo, formatNumber, plural } from '../lib/format'
import { pagePath } from '../lib/pages'
import type { Agent, Credential, ScanHost, ScanView, Site } from '../lib/types'
import { StatusBadge } from './Status'

/** Höchstens so viele Adressen je Scan; dieselbe Grenze prüfen Zentrale und Agent. */
export const SCAN_MAX_ADRESSEN = 1024

/** Trennt die Eingabe in Ziele: Leerzeichen, Komma, Semikolon, Zeilenumbruch. */
export function zieleAus(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((t) => t.trim())
    .filter(Boolean)
}

/** Wie viele Adressen ein Ziel umfasst, wie der Agent sie zählt (ohne Netz- und Broadcast-Adresse bei IPv4). */
export function adressenIn(ziel: string): number | null {
  const [addr, len] = ziel.split('/')
  if (!addr) return null
  const v4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(addr)
  const v6 = addr.includes(':')
  if (!v4 && !v6) return null
  if (len === undefined) return 1
  const bits = Number(len)
  const max = v4 ? 32 : 128
  if (!Number.isInteger(bits) || bits < 0 || bits > max) return null
  const host = max - bits
  if (host > 20) return Infinity
  const n = 2 ** host
  return v4 && bits < 31 ? n - 2 : n
}

const STATUS_TEXT: Record<ScanView['status'], string> = {
  running: 'Läuft',
  done: 'Fertig',
  failed: 'Abgebrochen',
  stopped: 'Angehalten',
}

function statusBadge(status: ScanView['status']) {
  const state = status === 'running' ? 'info' : status === 'done' ? 'ok' : status === 'failed' ? 'crit' : 'off'
  return <StatusBadge state={state} label={STATUS_TEXT[status]} />
}

interface Props {
  customerId?: string
  customerSlug: string | null
}

/**
 * Netzwerk scannen: der Agent eines Standorts pingt die Adressen eines
 * Bereichs und fragt jede mit den gewählten SNMP-Zugängen nach Name und
 * Kennung. Die Funde stehen als Vorschläge da; ein Gerät entsteht erst mit
 * „Als Gerät übernehmen“.
 */
export function NetzwerkScan({ customerId, customerSlug }: Props) {
  const queryClient = useQueryClient()
  const [offen, setOffen] = useState(false)
  const [siteId, setSiteId] = useState('')
  const [agentId, setAgentId] = useState('')
  const [zielText, setZielText] = useState('')
  const [zugaenge, setZugaenge] = useState<string[]>([])
  const [snmpPort, setSnmpPort] = useState('161')
  const [scanId, setScanId] = useState<string | null>(null)

  const sites = useQuery({
    queryKey: ['sites', customerId ?? null],
    queryFn: ({ signal }) => apiGet<Site[]>(customerId ? `/sites?kunde=${customerId}` : '/sites', signal),
    enabled: offen,
  })
  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: ({ signal }) => apiGet<Agent[]>('/agents', signal),
    enabled: offen,
  })
  const site = sites.data?.find((s) => s.id === siteId)
  // Zugänge zu sehen ist ein eigenes Recht. Ohne es läuft der Scan nur mit Ping.
  const credentials = useQuery({
    queryKey: ['credentials', site?.customer_id ?? null],
    queryFn: ({ signal }) => apiGet<Credential[]>(`/customers/${site!.customer_id}/credentials`, signal),
    enabled: Boolean(site),
  })
  const snmpZugaenge = (credentials.data ?? []).filter((c) => c.kind === 'snmp_v2c' || c.kind === 'snmp_v3')
  const letzte = useQuery({
    queryKey: ['scans', siteId],
    queryFn: ({ signal }) => apiGet<ScanView[]>(`/sites/${siteId}/scans`, signal),
    enabled: Boolean(siteId),
  })

  const agentsForSite = (agents.data ?? []).filter((a) => a.site_id === siteId)
  const agent = agentsForSite.find((a) => a.id === agentId)
  const ziele = zieleAus(zielText)
  const zaehlung = ziele.map(adressenIn)
  const ungueltig = ziele.filter((_, i) => zaehlung[i] === null)
  const summe = zaehlung.reduce<number>((a, n) => a + (n ?? 0), 0)
  const zuViel = summe > SCAN_MAX_ADRESSEN

  const start = useMutation({
    mutationFn: () =>
      apiSend<ScanView>('POST', `/sites/${siteId}/scans`, {
        agent_id: agentId,
        targets: ziele,
        credential_ids: zugaenge,
        snmp_port: Number(snmpPort) || 161,
      }),
    onSuccess: (scan) => {
      setScanId(scan.id)
      void queryClient.invalidateQueries({ queryKey: ['scans', siteId] })
    },
  })

  if (!offen) {
    return (
      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Netzwerk scannen</h2>
          <button type="button" className="btn" onClick={() => setOffen(true)}>
            Netzwerk scannen
          </button>
        </div>
        <div className="card-body muted" style={{ fontSize: 13, paddingTop: 0 }}>
          Der Agent eines Standorts sucht in einem Adressbereich nach Geräten; die Funde erscheinen als
          Vorschläge mit erkannter Vorlage.
        </div>
      </section>
    )
  }

  return (
    <section className="card" aria-labelledby="scan-titel">
      <div className="card-head">
        <h2 className="card-title" id="scan-titel">
          Netzwerk scannen
        </h2>
        <button type="button" className="btn btn-sm" onClick={() => setOffen(false)}>
          Schließen
        </button>
      </div>
      <form
        className="card-body stack"
        style={{ gap: 12 }}
        onSubmit={(e) => {
          e.preventDefault()
          if (siteId && agentId && ziele.length > 0 && ungueltig.length === 0 && !zuViel) start.mutate()
        }}
      >
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="scan-standort">Standort</label>
            <select
              id="scan-standort"
              className="input"
              value={siteId}
              onChange={(e) => {
                setSiteId(e.target.value)
                setAgentId('')
                setZugaenge([])
                setScanId(null)
              }}
            >
              <option value="">Bitte wählen</option>
              {(sites.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.customer_name} · {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 200 }}>
            <label htmlFor="scan-agent">Agent</label>
            <select
              id="scan-agent"
              className="input"
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={!siteId}
            >
              <option value="">Bitte wählen</option>
              {agentsForSite.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.connected ? '' : ' (nicht verbunden)'}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 260, flex: 1 }}>
            <label htmlFor="scan-ziele">Adressbereich</label>
            <input
              id="scan-ziele"
              className="input mono"
              value={zielText}
              onChange={(e) => setZielText(e.target.value)}
              placeholder="192.168.1.0/24"
            />
          </div>
          <div className="field" style={{ width: 100 }}>
            <label htmlFor="scan-port">SNMP-Port</label>
            <input
              id="scan-port"
              className="input mono"
              value={snmpPort}
              onChange={(e) => setSnmpPort(e.target.value)}
              inputMode="numeric"
            />
          </div>
        </div>

        {(agent?.networks ?? []).length > 0 ? (
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="muted" style={{ fontSize: 13 }}>
              Netze des Agents:
            </span>
            {agent!.networks!.map((n) => (
              <button
                key={n}
                type="button"
                className="chip mono"
                onClick={() => setZielText((t) => (zieleAus(t).includes(n) ? t : [...zieleAus(t), n].join(' ')))}
              >
                {n} übernehmen
              </button>
            ))}
          </div>
        ) : null}

        {site ? (
          credentials.isError ? (
            <span className="muted" style={{ fontSize: 13 }}>
              SNMP-Zugänge sind nur mit dem Recht „Zugangsdaten verwalten“ wählbar; der Scan läuft dann nur mit
              Ping.
            </span>
          ) : (
            <fieldset className="row" style={{ gap: 12, flexWrap: 'wrap', border: 'none', padding: 0, margin: 0 }}>
              <legend className="muted" style={{ fontSize: 13, marginBottom: 6 }}>
                SNMP-Zugänge, die probiert werden
              </legend>
              {snmpZugaenge.length === 0 ? (
                <span className="muted" style={{ fontSize: 13 }}>
                  Für diesen Kunden ist kein SNMP-Zugang hinterlegt; der Scan läuft nur mit Ping.
                </span>
              ) : (
                snmpZugaenge.map((c) => (
                  <label key={c.id} className="row" style={{ gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={zugaenge.includes(c.id)}
                      onChange={(e) =>
                        setZugaenge((z) => (e.target.checked ? [...z, c.id] : z.filter((x) => x !== c.id)))
                      }
                    />
                    {c.name}
                  </label>
                ))
              )}
            </fieldset>
          )
        ) : null}

        <div className="row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              start.isPending || !siteId || !agentId || ziele.length === 0 || ungueltig.length > 0 || zuViel
            }
          >
            {start.isPending ? 'Startet …' : 'Scan starten'}
          </button>
          {ungueltig.length > 0 ? (
            <span style={{ color: 'var(--crit)' }}>Kein gültiges Ziel: {ungueltig.join(', ')}</span>
          ) : zuViel ? (
            <span style={{ color: 'var(--crit)' }}>
              Mehr als {SCAN_MAX_ADRESSEN} Adressen; größere Netze bitte in Teilen scannen.
            </span>
          ) : ziele.length > 0 ? (
            <span className="muted">{plural(summe, 'Adresse', 'Adressen')}</span>
          ) : null}
          {start.isError ? <span style={{ color: 'var(--crit)' }}>{(start.error as Error).message}</span> : null}
        </div>
      </form>

      {scanId ? <ScanErgebnis scanId={scanId} customerSlug={customerSlug} /> : null}

      {!scanId && (letzte.data ?? []).length > 0 ? (
        <div className="card-body stack" style={{ gap: 6, paddingTop: 0 }}>
          <span className="muted" style={{ fontSize: 13 }}>
            Letzte Scans an diesem Standort
          </span>
          {(letzte.data ?? []).slice(0, 5).map((s) => (
            <div key={s.id} className="row" style={{ gap: 10 }}>
              {statusBadge(s.status)}
              <span className="mono">{s.targets.join(' ')}</span>
              <span className="muted">{formatAgo(s.started_at)}</span>
              <button type="button" className="btn btn-sm" onClick={() => setScanId(s.id)}>
                Ergebnis ansehen
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}

/** Fortschritt und Funde eines Scans; fragt nach, solange er läuft. */
export function ScanErgebnis({ scanId, customerSlug }: { scanId: string; customerSlug: string | null }) {
  const queryClient = useQueryClient()
  const scan = useQuery({
    queryKey: ['scan', scanId],
    queryFn: ({ signal }) => apiGet<ScanView>(`/scans/${scanId}`, signal),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2000 : false),
  })
  const stop = useMutation({
    mutationFn: () => apiSend<void>('POST', `/scans/${scanId}/stop`),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['scan', scanId] }),
  })

  if (scan.isError) {
    return (
      <div className="card-body" role="alert" style={{ color: 'var(--crit)' }}>
        {(scan.error as Error).message}
      </div>
    )
  }
  if (!scan.data) return <div className="card-body muted">Scan wird geladen …</div>
  const s = scan.data
  const anteil = s.total > 0 ? Math.round((s.scanned / s.total) * 100) : 0

  return (
    <div className="stack" style={{ gap: 0 }}>
      <div className="card-body row" style={{ gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingTop: 0 }}>
        {statusBadge(s.status)}
        <span role="status">
          {s.total > 0 ? `${s.scanned} von ${s.total} Adressen geprüft` : 'Wird gestartet …'} ·{' '}
          {plural(s.hosts.length, 'Fund', 'Funde')}
        </span>
        {s.status === 'running' ? (
          <>
            <progress max={100} value={anteil} aria-label="Fortschritt des Scans" style={{ width: 160 }} />
            <button type="button" className="btn btn-sm" disabled={stop.isPending} onClick={() => stop.mutate()}>
              Anhalten
            </button>
          </>
        ) : null}
        {s.error ? <span style={{ color: 'var(--crit)' }}>{s.error}</span> : null}
      </div>
      {s.hosts.length > 0 ? (
        <table className="data">
          <thead>
            <tr>
              <th>Adresse</th>
              <th>Name</th>
              <th>Erkannt als</th>
              <th>Antwort</th>
              <th>Systemtext</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {s.hosts.map((h) => (
              <FundZeile key={h.ip} scanId={s.id} host={h} customerSlug={customerSlug} />
            ))}
          </tbody>
        </table>
      ) : s.status !== 'running' ? (
        <div className="empty">
          <span>Im Bereich hat nichts geantwortet.</span>
        </div>
      ) : null}
    </div>
  )
}

function FundZeile({ scanId, host, customerSlug }: { scanId: string; host: ScanHost; customerSlug: string | null }) {
  const queryClient = useQueryClient()
  const [hinweis, setHinweis] = useState<string | null>(null)
  const adopt = useMutation({
    mutationFn: () =>
      apiSend<{ credential_assigned: boolean; hint?: string }>('POST', `/scans/${scanId}/uebernehmen`, {
        ip: host.ip,
      }),
    onSuccess: (resp) => {
      setHinweis(resp.hint ?? null)
      void queryClient.invalidateQueries({ queryKey: ['scan', scanId] })
      void queryClient.invalidateQueries({ queryKey: ['devices'] })
    },
  })
  const geraetId = host.adopted_device_id ?? host.existing_device_id
  return (
    <tr>
      <td className="mono">{host.ip}</td>
      <td>{host.sys_name || <span className="muted">–</span>}</td>
      <td>
        {host.template_name}{' '}
        {host.recognized ? (
          <StatusBadge state="ok" label="erkannt" />
        ) : (
          <StatusBadge state="off" label="allgemein" />
        )}
      </td>
      <td className="muted">
        {[
          host.pingable ? `Ping ${formatNumber(host.rtt_ms ?? 0, 'ms')}` : null,
          host.snmp ? 'SNMP' : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </td>
      <td className="muted" style={{ fontSize: 12, maxWidth: 320 }} title={host.sys_descr}>
        {host.sys_descr.length > 80 ? `${host.sys_descr.slice(0, 80)} …` : host.sys_descr}
      </td>
      <td>
        {geraetId ? (
          <Link to={`${pagePath('geraete', customerSlug)}/${geraetId}`}>
            {host.adopted_device_id ? 'Übernommen' : `Angelegt als ${host.existing_device_name}`}
          </Link>
        ) : (
          <button type="button" className="btn btn-sm" disabled={adopt.isPending} onClick={() => adopt.mutate()}>
            {adopt.isPending ? 'Übernimmt …' : 'Als Gerät übernehmen'}
          </button>
        )}
        {hinweis ? (
          <div className="muted" style={{ fontSize: 12 }}>
            {hinweis}
          </div>
        ) : null}
        {adopt.isError ? (
          <div style={{ color: 'var(--crit)', fontSize: 12 }}>{(adopt.error as Error).message}</div>
        ) : null}
      </td>
    </tr>
  )
}
